/**
 * API Client — thin call into the shared @shared/http factory.
 * Behaviour (timezone header, FormData passthrough, 429 retry-after,
 * refresh-token rotation, error toasts) is owned by the shared package.
 */
import { createApiClient } from "@shared/http";
import type { AxiosError, AxiosResponse } from "axios";
import { toast } from "sonner";
import {
	hasStepUpPrompt,
	requestStepUp,
	STEP_UP_REQUIRED_CODE,
} from "./step-up";
import { frontendTokenManager } from "./token-manager";

declare module "axios" {
	interface AxiosRequestConfig {
		/**
		 * The caller shows this request's failure itself, inline, so the global
		 * error, rate-limit and network toasts are skipped for it.
		 */
		inlineErrors?: boolean;
		/**
		 * This request changes the active role, so its `X-Active-Role` header
		 * names the role the caller is about to adopt, not a switch made
		 * elsewhere. The header is not reported for it.
		 */
		skipActiveRoleCheck?: boolean;
		/**
		 * Set on the second send of a request that got 403 STEP_UP_REQUIRED.
		 * A second refusal goes to the caller and does not open the OTP
		 * dialog again.
		 */
		stepUpRetried?: boolean;
	}
}

export const ACTIVE_ROLE_HEADER = "x-active-role";
export const ROLE_NOT_ACTIVE_CODE = "ROLE_NOT_ACTIVE";

/**
 * What the auth store does with the role signals on a response. Registered
 * from the store, so this module does not import it.
 */
export interface RoleResponseObserver {
	/** The `X-Active-Role` header of any response. */
	activeRole: (role: string) => void;
	/** The role named by a 403 `ROLE_NOT_ACTIVE`. */
	roleNotActive: (role: string) => void;
}

let roleObserver: RoleResponseObserver | null = null;

/**
 * Requests whose 403 `ROLE_NOT_ACTIVE` was reported to the observer while a
 * switch prompt was mounted to show it. The generic error toast is skipped
 * for them.
 */
const roleNotActiveRequests = new WeakSet<object>();
let mountedRoleNotActivePrompts = 0;

/**
 * Called by the ROLE_NOT_ACTIVE switch prompt while it is mounted. Returns
 * the function that unregisters it.
 */
export function registerRoleNotActivePrompt(): () => void {
	mountedRoleNotActivePrompts += 1;
	return () => {
		mountedRoleNotActivePrompts -= 1;
	};
}

let sessionLostHandler: (() => void) | null = null;

/**
 * Register what the auth store does when the server refuses to refresh the
 * session. Registered from the store, so this module does not import it.
 */
export function observeSessionLoss(handler: (() => void) | null): void {
	sessionLostHandler = handler;
}

export function observeRoleResponses(
	observer: RoleResponseObserver | null,
): void {
	roleObserver = observer;
}

/**
 * Requests that got 403 STEP_UP_REQUIRED while the OTP dialog was mounted.
 * The generic error toast is skipped for them, and the step-up interceptor
 * below opens the dialog and sends them again.
 */
const stepUpRequests = new WeakSet<object>();

function readHeader(response: AxiosResponse, name: string): unknown {
	const headers = response.headers as
		| { get?: (header: string) => unknown; [key: string]: unknown }
		| undefined;
	if (typeof headers?.get === "function") return headers.get(name);
	return headers?.[name];
}

function markStepUpRefusal(response: AxiosResponse): void {
	const body = response.data as { error?: unknown } | undefined;
	if (
		response.status === 403 &&
		body?.error === STEP_UP_REQUIRED_CODE &&
		response.config &&
		!response.config.stepUpRetried &&
		hasStepUpPrompt()
	) {
		stepUpRequests.add(response.config);
	}
}

function reportRoleSignals(response: AxiosResponse): void {
	if (!roleObserver) return;

	const activeRole = readHeader(response, ACTIVE_ROLE_HEADER);
	if (
		typeof activeRole === "string" &&
		activeRole !== "" &&
		!response.config?.skipActiveRoleCheck
	) {
		roleObserver.activeRole(activeRole);
	}

	const body = response.data as
		| { code?: unknown; data?: { role?: unknown } | null }
		| undefined;
	if (
		response.status === 403 &&
		body?.code === ROLE_NOT_ACTIVE_CODE &&
		typeof body.data?.role === "string"
	) {
		roleObserver.roleNotActive(body.data.role);
		if (response.config && mountedRoleNotActivePrompts > 0) {
			roleNotActiveRequests.add(response.config);
		}
	}
}

// The shared client hands each toast callback the failing request, so the
// opt-out is decided per request (request.inlineErrors). No global state:
// a pending opt-out request can no longer suppress another request's toast
// (the counter race from PR #360 review), and a 401-refresh retry cannot
// leak the count.

// 3000, matching the backend's PORT default (config/env.ts:40) and
// .env.example. The fallback said 3200, which is not a port anything in this
// repo listens on -- so a developer with no .env got connection refused.
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export const apiClient = createApiClient({
	baseUrl: API_URL,
	tokenManager: frontendTokenManager,
	// The shared client calls this after it clears the stored tokens. This
	// callback does not go to /login: the auth store drops the user, then
	// `ProtectedRoute` sends a visitor on a protected route to /login. A
	// public page stays where it is.
	onUnauthorized: () => {
		sessionLostHandler?.();
	},
	onResponse: (response) => {
		markStepUpRefusal(response);
		reportRoleSignals(response);
	},
	onError: (_status, message, request) => {
		if (request?.inlineErrors) return;
		if (request && roleNotActiveRequests.has(request)) return;
		if (request && stepUpRequests.has(request)) return;
		toast.error(message);
	},
	onRateLimited: (retryAfter, request) => {
		if (request?.inlineErrors) return;
		toast.error(
			`Too many requests. Please try again in ${retryAfter} seconds.`,
		);
	},
	onNetworkError: (request) => {
		if (request?.inlineErrors) return;
		// A response-less axios error also covers server crashes with no body
		// and timeouts, not just a dropped connection -- don't assert a cause
		// we can't actually tell apart from here.
		toast.error("Something went wrong. Please try again.");
	},
});

// Registered after createApiClient, so it runs after the shared interceptor.
// That interceptor has already called onError, which skips the toast for a
// request in stepUpRequests.
apiClient.interceptors.response.use(undefined, async (error: AxiosError) => {
	const config = error.config;
	if (!config || !stepUpRequests.has(config)) throw error;
	stepUpRequests.delete(config);
	if (!(await requestStepUp())) throw error;
	config.stepUpRetried = true;
	return apiClient(config);
});

apiClient.defaults.timeout = 30000;

export default apiClient;
